import { MessageMap, WebviewId } from '@gitlab-org/webview-plugin';
import { Connection } from 'vscode-languageserver';
import { Disposable } from '@gitlab-org/disposable';
import { MessageBus, ExtractRequestResult } from '@gitlab-org/message-bus';
import { RpcMethods, Handlers } from './types';

type ExtensionConnectionMessageBusProps = {
  webviewId: WebviewId;
  connection: Connection;
  rpcMethods: RpcMethods;
  handlers: Handlers;
};

export class ExtensionConnectionMessageBus<TMessageMap extends MessageMap = MessageMap>
  implements MessageBus<TMessageMap>
{
  #webviewId: WebviewId;

  #connection: Connection;

  #rpcMethods: RpcMethods;

  #handlers: Handlers;

  constructor({ webviewId, connection, rpcMethods, handlers }: ExtensionConnectionMessageBusProps) {
    this.#webviewId = webviewId;
    this.#connection = connection;
    this.#rpcMethods = rpcMethods;
    this.#handlers = handlers;
  }

  onRequest<T extends keyof TMessageMap['inbound']['requests'] & string>(
    type: T,
    handler: (
      payload: TMessageMap['inbound']['requests'][T]['params'],
    ) => Promise<ExtractRequestResult<TMessageMap['inbound']['requests'][T]>>,
  ): Disposable {
    return this.#handlers.request.register({ webviewId: this.#webviewId, type }, handler);
  }

  onNotification<T extends keyof TMessageMap['inbound']['notifications'] & string>(
    type: T,
    handler: (payload: TMessageMap['inbound']['notifications'][T]) => void,
  ): Disposable {
    return this.#handlers.notification.register({ webviewId: this.#webviewId, type }, handler);
  }

  sendRequest<T extends keyof TMessageMap['outbound']['requests'] & string>(
    type: T,
    payload?: TMessageMap['outbound']['requests'][T]['params'],
  ): Promise<ExtractRequestResult<TMessageMap['outbound']['requests'][T]>> {
    return this.#connection.sendRequest(this.#rpcMethods.request, {
      pluginId: this.#webviewId,
      type,
      payload,
    });
  }

  async sendNotification<T extends keyof TMessageMap['outbound']['notifications'] & string>(
    type: T,
    payload?: TMessageMap['outbound']['notifications'][T],
  ): Promise<void> {
    await this.#connection.sendNotification(this.#rpcMethods.notification, {
      pluginId: this.#webviewId,
      type,
      payload,
    });
  }
}
